import React, { useEffect, useRef, useState } from 'react';
import { 
  FaFileContract, FaCheckCircle, FaExclamationTriangle,
  FaGavel, FaCopyright, FaShieldAlt, FaUserGraduate,
  FaBookOpen, FaLaptop, FaPhone, FaEnvelope, FaMapMarkerAlt,
  FaArrowRight, FaRegClock, FaUsers, FaGlobe, FaBan,
  FaHandshake, FaBalanceScale, FaUniversity
} from 'react-icons/fa';
import { MdVerified, MdSecurity, MdDescription } from 'react-icons/md';

const TermsConditions = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const [lastUpdated] = useState("15 April 2024");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const termsSections = [
    {
      title: "Acceptance of Terms",
      icon: <FaHandshake className="text-blue-500" />,
      content: "By accessing and using the BNS Mahila Maha Vidyalaya website, you agree to comply with these Terms & Conditions. If you do not agree, please do not use our website or services. These terms apply to all visitors, users, and others who access or use the website."
    },
    {
      title: "Use of Website",
      icon: <FaLaptop className="text-blue-500" />,
      content: "The content on this website is for informational purposes only. You agree to use the website only for lawful purposes, not to copy, reproduce, distribute, or modify any content without prior written consent, not to attempt to gain unauthorized access, and not to use any automated means to access the website."
    },
    {
      title: "Admission Terms",
      icon: <FaUserGraduate className="text-blue-500" />,
      content: "Admission is granted based on eligibility criteria and merit. All documents submitted must be authentic and verifiable. Fees once paid are non-refundable as per refund policy. The college reserves the right to cancel admission if false information is provided. Admission is provisional until verification of original documents."
    },
    {
      title: "Code of Conduct",
      icon: <FaShieldAlt className="text-blue-500" />,
      content: "Students must maintain discipline and respect for faculty and staff, follow academic integrity policies, attend classes regularly and punctually, wear prescribed uniform/ID card on campus, not engage in any form of ragging or harassment, and keep the campus clean and eco-friendly."
    },
    {
      title: "Intellectual Property",
      icon: <FaCopyright className="text-blue-500" />,
      content: "All content, logos, images, and materials on this website are the intellectual property of BNS Mahila Maha Vidyalaya and are protected by copyright laws. Unauthorized use of any material may violate copyright, trademark, and other laws."
    },
    {
      title: "Limitation of Liability",
      icon: <FaExclamationTriangle className="text-blue-500" />,
      content: "BNS Mahila Maha Vidyalaya shall not be liable for any direct, indirect, incidental, or consequential damages arising from the use or inability to use our website or services. This includes damages for errors, omissions, interruptions, defects, delays, or viruses."
    },
    {
      title: "Third-Party Links",
      icon: <FaGlobe className="text-blue-500" />,
      content: "Our website may contain links to third-party websites. We are not responsible for the content, privacy policies, or practices of any third-party websites. You access such websites at your own risk."
    },
    {
      title: "Termination",
      icon: <FaBan className="text-blue-500" />,
      content: "We may terminate or suspend your access to our website immediately, without prior notice, for conduct that violates these Terms & Conditions or is harmful to other users or the college."
    },
    {
      title: "Changes to Terms",
      icon: <MdDescription className="text-blue-500" />,
      content: "We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting on this page. Your continued use of the website constitutes acceptance of the modified terms."
    },
    {
      title: "Governing Law",
      icon: <FaBalanceScale className="text-blue-500" />,
      content: "These Terms & Conditions shall be governed by and construed in accordance with the laws of India. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts in Varanasi, Uttar Pradesh."
    }
  ];

  const prohibitedActions = [
    "Violating any applicable laws or regulations",
    "Uploading malicious code or viruses",
    "Attempting to gain unauthorized access to our systems",
    "Interfering with the proper functioning of the website",
    "Harassing, abusing, or harming others",
    "Impersonating any person or entity",
    "Collecting user information without consent",
    "Using automated systems to access the website"
  ];

  const userResponsibilities = [
    "Provide accurate and complete information",
    "Keep login credentials secure and confidential",
    "Notify us immediately of any unauthorized access",
    "Comply with all applicable laws and regulations",
    "Respect the rights of other users",
    "Use the website only for legitimate purposes"
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-blue-50 via-white to-blue-100 py-16 min-h-screen">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-blue-500 rounded-full"></div>
            <div className="w-8 h-1 bg-blue-600 rounded-full"></div>
            <div className="w-6 h-1 bg-blue-700 rounded-full"></div>
          </div>

          {/* Breadcrumb */}
          <div className={`flex items-center justify-center gap-2 text-xs mb-6 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
          }`}>
            <span className="text-gray-500">Home</span>
            <span className="text-gray-400">/</span>
            <span className="text-blue-600 font-semibold">Terms & Conditions</span>
          </div>

          {/* Heading */}
          <div className={`text-center mb-10 transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="flex justify-center mb-3">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-3 rounded-2xl shadow-lg">
                <FaFileContract className="text-white text-3xl" />
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="text-blue-800">Terms & Conditions</span>
            </h2>
            <div className="w-24 h-1 bg-blue-500 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4">
              Last Updated: {lastUpdated}
            </p>
          </div>

          {/* Introduction Card */}
          <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="flex items-start gap-3">
              <FaGavel className="text-blue-600 text-2xl mt-1" />
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Agreement to Terms</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  By accessing or using the website of <span className="font-semibold text-blue-600">BNS Mahila Maha Vidyalaya</span>, 
                  you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions. 
                  These terms constitute a legally binding agreement between you and the college.
                </p>
              </div>
            </div>
          </div>

          {/* Terms Sections Grid */}
          <div className={`grid md:grid-cols-2 gap-5 mb-8 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {termsSections.map((section, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="border-l-4 border-blue-500 p-4 h-full">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      {section.icon}
                    </div>
                    <h3 className="font-bold text-gray-800">{section.title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed pl-11">
                    {section.content}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Prohibited Actions & User Responsibilities */}
          <div className={`grid md:grid-cols-2 gap-6 mb-8 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {/* Prohibited Actions */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="bg-red-50 p-4 border-b border-red-100">
                <h3 className="font-bold text-red-700 flex items-center gap-2">
                  <FaBan className="text-red-500" />
                  Prohibited Actions
                </h3>
              </div>
              <div className="p-4">
                <ul className="space-y-2">
                  {prohibitedActions.map((action, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <FaExclamationTriangle className="text-red-500 text-xs mt-0.5" />
                      <span className="text-gray-600 text-sm">{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* User Responsibilities */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="bg-green-50 p-4 border-b border-green-100">
                <h3 className="font-bold text-green-700 flex items-center gap-2">
                  <FaCheckCircle className="text-green-500" />
                  User Responsibilities
                </h3>
              </div>
              <div className="p-4">
                <ul className="space-y-2">
                  {userResponsibilities.map((resp, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <FaCheckCircle className="text-green-500 text-xs mt-0.5" />
                      <span className="text-gray-600 text-sm">{resp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Disclaimer Section */}
          <div className={`bg-yellow-50 rounded-xl p-5 mb-8 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="flex items-start gap-3">
              <MdSecurity className="text-yellow-600 text-xl" />
              <div>
                <h3 className="font-bold text-gray-800 mb-2">Disclaimer of Warranties</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  The website and its content are provided "as is" without warranties of any kind, either express or implied. 
                  BNS Mahila Maha Vidyalaya does not warrant that the website will be uninterrupted, error-free, or free of 
                  viruses or other harmful components. We reserve the right to modify, suspend, or discontinue any aspect 
                  of the website at any time without notice.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className={`bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-6 text-white transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="text-center">
              <h3 className="text-xl font-bold mb-3">Questions About Terms?</h3>
              <p className="text-blue-100 text-sm mb-4">Contact our legal department for any questions regarding these terms</p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2">
                  <FaPhone className="text-yellow-400" />
                  <span className="text-sm">+91 7080 100 333</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaEnvelope className="text-yellow-400" />
                  <span className="text-sm">legal@bnsmahila.edu</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaMapMarkerAlt className="text-yellow-400" />
                  <span className="text-sm">Akhari Bai-Pass, Varanasi, UP 221011</span>
                </div>
              </div>
            </div>
          </div>

          {/* Acknowledgment Note */}
          <div className={`mt-6 bg-blue-50 rounded-lg p-4 text-center transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <p className="text-blue-700 text-xs">
              <span className="font-semibold">Acknowledgment:</span> By using this website, you acknowledge that you have read, 
              understood, and agree to be bound by these Terms & Conditions. If you do not agree to these terms, please do not use this website.
            </p>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-700 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default TermsConditions;